import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  TextField,
  Grid,
  InputAdornment,
  Avatar,
} from "@mui/material";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import { useNavigate } from "react-router-dom";
import config from "../../config";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const initials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "").concat(parts[1]?.[0] || "").toUpperCase() || name[0].toUpperCase();
};

export default function DeletedMandiPartner() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchDeletedPartners = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/mandi-partner/deleted-partners`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        setPartners(Array.isArray(data.data) ? data.data : []);
      } else {
        setPartners([]);
      }
    } catch (error) {
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletedPartners();
  }, [fetchDeletedPartners]);

  const filteredPartners = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    if (!term) return partners;

    return partners.filter((p) =>
      [p.mandiPartner, p.mandiLocation, p.state, p.mandiCode, p.businessName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [partners, searchInput]);

  const handleClearSearch = () => setSearchInput("");

  const openRestoreDialog = (partner) => {
    setSelectedPartner(partner);
    setRestoreDialog(true);
  };

  const closeRestoreDialog = () => {
    if (!restoreLoading) {
      setRestoreDialog(false);
      setSelectedPartner(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedPartner) return;

    try {
      setRestoreLoading(true);
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        `${config.apiBaseUrl}/mandi-partner/restore-deleted-partners/${selectedPartner._id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();

      setRestoreDialog(false);
      setSelectedPartner(null);

      if (res.ok && data.success !== false) {
        setSnackbar({
          open: true,
          message: data.message || "Mandi partner restored successfully",
          severity: "success",
        });
        await fetchDeletedPartners();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to restore mandi partner",
          severity: "error",
        });
      }
    } catch (error) {
      setRestoreDialog(false);
      setSelectedPartner(null);

      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setRestoreLoading(false);
    }
  };

  const isCompact = isMobile;

  const headCellSx = {
    px: isCompact ? 1 : 2,
    py: isCompact ? 1 : 1.35,
    fontSize: isCompact ? "0.6rem" : "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#475569",
    background: "#F1F5F9",
    borderBottom: "2px solid #E2E8F0",
    whiteSpace: "nowrap",
  };
  const bodyCellSx = {
    px: isCompact ? 1 : 2,
    py: isCompact ? 1 : 1.25,
    fontSize: isCompact ? "0.7rem" : "0.82rem",
    borderBottom: "1px solid #EEF2F6",
    whiteSpace: "nowrap",
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", background: "#F8FAFC", overflow: "hidden" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 4 },
            pb: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mb={3}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{ border: "1px solid #E5E7EB", borderRadius: "12px" }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <IconButton
                onClick={() => navigate("/MandiPartner")}
                sx={{ border: "1px solid #E5E7EB", borderRadius: "12px" }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>

              <Box>
                <Typography variant="h4" fontWeight={800} lineHeight={1.2}>
                  Deleted Mandi Partners
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B", mt: 0.25 }}>
                  Partners removed from the active list — restore any of them below.
                </Typography>
              </Box>

              {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Stack>

            {!loading && (
              <Chip
                icon={<InventoryRoundedIcon sx={{ fontSize: 16 }} />}
                label={`${partners.length} deleted`}
                sx={{
                  fontWeight: 700,
                  bgcolor: "#FEF2F2",
                  color: "#B91C1C",
                  px: 0.5,
                }}
              />
            )}
          </Stack>

          <Paper
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              flexShrink: 0,
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={8} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, location, state, mandi code…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ position: "relative", flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Paper
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <TableContainer
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <Table stickyHeader size={isMobile ? "small" : "medium"} sx={{ minWidth: isMobile ? 520 : 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 150 : 200 }}>Partner</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 70 : 85 }}>Mandi Code</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 90 : 115 }}>Location</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 65 : 85 }}>State</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 85 : 110 }}>Deleted On</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 72 : 95 }} align="center">
                        Restore
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6, border: "none" }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : filteredPartners.length > 0 ? (
                      filteredPartners.map((partner) => (
                        <TableRow
                          key={partner._id}
                          hover
                          sx={{
                            transition: "background-color 0.15s ease",
                            "&:hover": { backgroundColor: "#F8FAFC" },
                          }}
                        >
                          <TableCell sx={bodyCellSx}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  bgcolor: "#EFF6FF",
                                  color: "#2563EB",
                                }}
                              >
                                {initials(partner.mandiPartner)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: "inherit", fontWeight: 600, color: "#1E293B" }}>
                                  {partner.mandiPartner || "—"}
                                </Typography>
                                {partner.businessName && (
                                  <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                                    {partner.businessName}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              label={partner.mandiCode || "—"}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: "#F1F5F9",
                                color: "#334155",
                                height: isCompact ? 20 : 22,
                                fontSize: isCompact ? "0.6rem" : "0.68rem",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{partner.mandiLocation || "—"}</TableCell>
                          <TableCell sx={bodyCellSx}>{partner.state || "—"}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography sx={{ fontSize: "inherit", color: "#64748B" }}>
                              {formatDate(partner.updatedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx }} align="center">
                            <Tooltip title="Restore this partner">
                              <IconButton
                                size="small"
                                onClick={() => openRestoreDialog(partner)}
                                sx={{
                                  color: "#16A34A",
                                  border: "1px solid #DCFCE7",
                                  bgcolor: "#F0FDF4",
                                  "&:hover": { bgcolor: "#DCFCE7", borderColor: "#BBF7D0" },
                                }}
                              >
                                <RestoreRoundedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6, border: "none" }}>
                          <Stack alignItems="center" spacing={1}>
                            <InventoryRoundedIcon sx={{ fontSize: 32, color: "#CBD5E1" }} />
                            <Typography sx={{ color: "#64748B" }}>No deleted mandi partners found</Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {!loading && (
              <Typography variant="caption" sx={{ color: "#94A3B8", display: "block", mt: 1.25, mb: 1.5, textAlign: "center" }}>
                Showing {filteredPartners.length} of {partners.length} total
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog open={restoreDialog} onClose={closeRestoreDialog} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle fontWeight={700}>Restore Mandi Partner</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Restore <strong>{selectedPartner?.mandiPartner}</strong> from{" "}
            <strong>{selectedPartner?.mandiLocation}</strong>? It will reappear in the active mandi partners list.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeRestoreDialog} disabled={restoreLoading} sx={{ textTransform: "none" }}>
            Cancel
          </Button>

          <Button
            color="success"
            variant="contained"
            disableElevation
            onClick={handleRestore}
            disabled={restoreLoading}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            {restoreLoading ? "Restoring..." : "Restore"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}